/**
 * Input/output contracts for the Event bulk callables.
 *
 * Single-event creation goes through direct Firestore writes
 * (eventService.createEvent). The bulk path lives here for two reasons:
 *   - Organizers want to spin a full event up from a workbook, not just
 *     create the shell and hand-fill the FAQ section.
 *   - Side-effects (venueLocationId resolution, organizer self-join via
 *     inviteToEvent, role-audience bootstrap via the create trigger)
 *     belong server-side; the bulk callable owns them in one transaction
 *     per event.
 *
 * The input takes a list of events for shape symmetry with the other
 * bulk callables, even though the Event sheet only carries one row per
 * workbook (the operator's vertical KV layout).
 *
 * Bulk-create is **admin-only** in v1 — event creation is a tightly
 * scoped action and the callable's authorization check refuses anything
 * less than a global admin claim. (Single-event creation in the admin
 * form is also admin-gated by the parent navigation surface.)
 */

import { z } from 'zod';

export const bulkCreateEventsFaqEntrySchema = z
  .object({
    question: z.string().min(1).max(500),
    answer: z.string().min(1).max(2000),
  })
  .strict();

export type BulkCreateEventsFaqEntry = z.infer<
  typeof bulkCreateEventsFaqEntrySchema
>;

export const bulkCreateEventsRowSchema = z
  .object({
    name: z.string().min(1).max(200),
    description: z.string().nullable(),
    /** YYYY-MM-DD, event-local. */
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** YYYY-MM-DD, event-local. */
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** IANA timezone (e.g. "America/New_York"). */
    timezone: z.string().min(1),
    status: z.enum(['draft', 'published', 'archived']),
    /**
     * Optional venue label. When set, the callable either reuses an
     * existing eventLocation with the same label (case-insensitive match
     * within the new event) or creates a fresh `verified: false`
     * eventLocation in the same write batch and points
     * `venueLocationId` at it.
     */
    venueLabel: z.string().nullable(),
    /**
     * Free-text address line stored on the venue eventLocation row when
     * one is auto-created. Ignored when no venueLabel is provided.
     */
    venueAddress: z.string().nullable(),
    /**
     * Optional hero image URL or path. Stored verbatim on the event.
     * Bulk import does not upload binaries; this is a reference to an
     * already-hosted asset (seed image path or pre-uploaded URL).
     */
    heroImage: z.string().nullable(),
    /** Optional hero title override (custom display text over the hero). */
    heroTitleOverride: z.string().nullable(),
    /** Per-event FAQ entries written into Event.config.faqs. */
    faqs: z.array(bulkCreateEventsFaqEntrySchema),
  })
  .strict();

export type BulkCreateEventsRow = z.infer<typeof bulkCreateEventsRowSchema>;

export const bulkCreateEventsInputSchema = z
  .object({
    rows: z.array(bulkCreateEventsRowSchema).min(1).max(50),
  })
  .strict();

export type BulkCreateEventsInput = z.infer<
  typeof bulkCreateEventsInputSchema
>;

export type BulkCreateEventsRowStatus =
  | 'created'
  | 'duplicate_name'
  | 'failed';

export interface BulkCreateEventsRowResult {
  index: number;
  name: string;
  status: BulkCreateEventsRowStatus;
  /** New event document ID, populated when status === 'created'. */
  eventId?: string;
  /**
   * New eventLocation document ID created for the venue, when
   * `venueLabel` was set and we wrote a fresh row. Null when reusing
   * an existing location or when no venue was supplied.
   */
  venueLocationId?: string | null;
  /** Populated for any non-created status. */
  message?: string;
}

export interface BulkCreateEventsResponse {
  success: true;
  results: BulkCreateEventsRowResult[];
  summary: {
    created: number;
    duplicateName: number;
    failed: number;
  };
}

// ---------------------------------------------------------------------------
// bulkResolveEvents - preview pass for the admin import dialog.
//
// Same input as bulkCreate. Does NOT write. Reports per row whether the
// row would be created fresh or would collide with an existing event
// name (case-insensitive). Also surfaces whether the venue label
// already exists in any event we might choose to attach (deferred -
// the bulk-create callable currently only matches venues within the
// newly created event; cross-event reuse is intentionally a v2 feature).
// ---------------------------------------------------------------------------

export const bulkResolveEventsInputSchema = bulkCreateEventsInputSchema;

export type BulkResolveEventsInput = z.infer<
  typeof bulkResolveEventsInputSchema
>;

export type BulkResolveEventsRowStatus =
  | 'new'
  | 'matches_existing'
  | 'failed';

export interface BulkResolveEventsProposal {
  /** Where the canonical name came from. */
  source: 'typed' | 'existing';
  /** When source='existing': the doc ID of the matching event. */
  matchedExistingEventId?: string;
  /** Final name the row would land at. */
  canonicalName: string;
  /** Echo of the FAQ pair count for the reviewing surface. */
  faqCount: number;
}

export interface BulkResolveEventsRowResult {
  index: number;
  typedName: string;
  status: BulkResolveEventsRowStatus;
  proposal?: BulkResolveEventsProposal;
  message?: string;
}

export interface BulkResolveEventsResponse {
  success: true;
  results: BulkResolveEventsRowResult[];
  summary: {
    new: number;
    matchesExisting: number;
    failed: number;
  };
}
